import { apiClient } from '@/shared/api';

import type {
  ProductFamily,
  ProductFamilyAttribute,
  ProductFamilyAttributeInput,
  ProductFamilyAttributeUpdateRequest,
  ProductFamilyCreateRequest,
  ProductFamilyUpdateRequest,
} from '../model/types';

const BASE = '/api/v1/catalog/product-families';

/** List the tenant's attribute templates (epic-catalog-004, user-story-catalog-005). */
export const listProductFamilies = async (): Promise<ProductFamily[]> =>
  (await apiClient.get<ProductFamily[]>(BASE)).data;

export const getProductFamily = async (code: string): Promise<ProductFamily> =>
  (await apiClient.get<ProductFamily>(`${BASE}/${encodeURIComponent(code)}`)).data;

export const createProductFamily = async (
  body: ProductFamilyCreateRequest,
): Promise<ProductFamily> => (await apiClient.post<ProductFamily>(BASE, body)).data;

/** `code` is immutable. Include `version` from the last read for optimistic locking (AC6). */
export const updateProductFamily = async (
  code: string,
  body: ProductFamilyUpdateRequest,
): Promise<ProductFamily> =>
  (await apiClient.patch<ProductFamily>(`${BASE}/${encodeURIComponent(code)}`, body)).data;

export const deleteProductFamily = async (code: string): Promise<void> => {
  await apiClient.delete(`${BASE}/${encodeURIComponent(code)}`);
};

export const addProductFamilyAttribute = async (
  code: string,
  body: ProductFamilyAttributeInput,
): Promise<ProductFamilyAttribute> =>
  (
    await apiClient.post<ProductFamilyAttribute>(
      `${BASE}/${encodeURIComponent(code)}/attributes`,
      body,
    )
  ).data;

export const updateProductFamilyAttribute = async (
  code: string,
  attributeCode: string,
  body: ProductFamilyAttributeUpdateRequest,
): Promise<ProductFamilyAttribute> =>
  (
    await apiClient.patch<ProductFamilyAttribute>(
      `${BASE}/${encodeURIComponent(code)}/attributes/${encodeURIComponent(attributeCode)}`,
      body,
    )
  ).data;

/** Rejected with `409` if a product on this family already has a value for the attribute (AC4). */
export const deleteProductFamilyAttribute = async (
  code: string,
  attributeCode: string,
): Promise<void> => {
  await apiClient.delete(
    `${BASE}/${encodeURIComponent(code)}/attributes/${encodeURIComponent(attributeCode)}`,
  );
};
